import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Download, MessageCircle, Loader2 } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUS_META, StatusBadge } from "@/components/admin/StatusBadge";
import ScoreGauge from "@/components/funnel/ScoreGauge";
import { api, pdfUrl, formatApiError } from "@/lib/api";

const LeadHeader = ({ lead }) => (
  <div className="flex items-center gap-4">
    <ScoreGauge score={lead.visibility_score} rating={lead.rating} size={120} />
    <div>
      <div className="font-heading text-2xl font-bold text-obsidian">{lead.brand}</div>
      <div className="font-data text-sm text-neutral-500">{lead.domain}</div>
      <div className="mt-2"><StatusBadge status={lead.status} /></div>
    </div>
  </div>
);

const LeadMeta = ({ lead }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className="border-2 border-[rgba(10,10,10,0.1)] p-3">
      <div className="av-label">WhatsApp</div>
      <div className="font-data text-obsidian mt-1">{lead.phone_full}</div>
    </div>
    <div className="border-2 border-[rgba(10,10,10,0.1)] p-3">
      <div className="av-label">Source</div>
      <div className="font-data text-obsidian mt-1 uppercase">{lead.source_campaign}</div>
    </div>
  </div>
);

const StatusSelect = ({ status, onChange }) => (
  <div>
    <div className="av-label mb-2">Update Status</div>
    <Select value={status} onValueChange={onChange}>
      <SelectTrigger className="w-full border-2 border-obsidian rounded-none font-data" data-testid="status-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(STATUS_META).map(([k, v]) => (
          <SelectItem key={k} value={k}>{v.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const MessageLog = ({ messages }) => (
  <div>
    <div className="av-label mb-2">Message Log</div>
    <div className="space-y-3">
      {messages?.map((mm) => (
        <div key={mm.id} className="border-2 border-[rgba(10,10,10,0.1)] p-3">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />
            <span className="av-label">{mm.stage} · {mm.status}</span>
          </div>
          <p className="font-data text-xs text-neutral-600 whitespace-pre-line line-clamp-4">{mm.body}</p>
        </div>
      ))}
    </div>
  </div>
);

const FollowupList = ({ steps }) => (
  <div>
    <div className="av-label mb-2">Follow-up Sequence</div>
    <div className="space-y-2">
      {steps.map((s) => (
        <div key={s.stage} className="flex items-center justify-between border-b border-[rgba(10,10,10,0.08)] py-2">
          <span className="text-sm text-obsidian">{s.label}</span>
          <span className="font-data text-[10px] uppercase tracking-wider px-2 py-0.5 border" style={{ color: s.status === "sent" ? "#00C853" : "#525252", borderColor: s.status === "sent" ? "#00C853" : "#525252" }}>
            {s.status}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export const LeadDetail = ({ leadId, open, onClose, onStatusChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!leadId || !open) return;
    setData(null);
    setLoading(true);
    api.leadDetail(leadId)
      .then(setData)
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [leadId, open]);

  const changeStatus = async (status) => {
    try {
      await api.updateStatus(leadId, status);
      setData((d) => ({ ...d, lead: { ...d.lead, status } }));
      onStatusChange?.();
      toast.success(`Status → ${STATUS_META[status]?.label}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const lead = data?.lead;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-0 border-l-2 border-obsidian" data-testid="lead-detail">
        <SheetHeader className="p-6 border-b-2 border-obsidian bg-obsidian text-white">
          <SheetTitle className="text-white font-heading flex items-center justify-between">
            <span>Lead Detail</span>
            <button onClick={onClose} data-testid="close-detail"><X className="h-5 w-5" /></button>
          </SheetTitle>
          <SheetDescription className="sr-only">Lead details, message history and status controls</SheetDescription>
        </SheetHeader>

        {loading && <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-conversion" /></div>}

        {lead && (
          <div className="p-6 space-y-6">
            <LeadHeader lead={lead} />
            <LeadMeta lead={lead} />
            <StatusSelect status={lead.status} onChange={changeStatus} />

            <a href={pdfUrl(lead.id)} target="_blank" rel="noreferrer" className="av-btn-outline w-full" data-testid="detail-download-pdf">
              <Download className="h-4 w-4" /> Download Report PDF
            </a>

            <div>
              <div className="av-label mb-2">Summary</div>
              <p className="text-sm text-neutral-700 leading-relaxed">{lead.summary}</p>
            </div>

            <MessageLog messages={data.messages} />
            {data.followups?.steps && <FollowupList steps={data.followups.steps} />}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default LeadDetail;
